require("./calcalist_site_articleImageGalleryV2.less")
import { useEffect, useRef, useState } from "react"
import Slider from "react-slick"

export interface ArticleImageGalleryV2Item {
    imageId: string
    imageSrc: string
    caption: string
    credit: string
}

interface ArticleImageGalleryV2ViewProps {
    imageList: ArticleImageGalleryV2Item[]
    isMobile?: boolean
    isApp?: boolean
    componentaId?: string
}

const emptyItem: ArticleImageGalleryV2Item = { imageId: "", imageSrc: "", caption: "", credit: "" }

const GalleryDots = (props: { imageList: ArticleImageGalleryV2Item[], currentSlide: number, onGoTo: (index: number) => void }) => (
    <div className="galleryDots">
        {props.imageList.map((image, index) =>
            <button key={image.imageId || index} type="button" className={"galleryDot" + (index === props.currentSlide ? " isActive" : "")} aria-label={String(index + 1)} onClick={() => props.onGoTo(index)} />
        )}
    </div>
)

const GalleryCaption = (props: { current: ArticleImageGalleryV2Item, inline?: boolean }) => {
    const { current, inline } = props
    const credit = current.credit ? `(${current.credit})` : ""
    const title = current.caption
        ? (inline
            ? <span className="galleryCaptionTitle">{current.caption} </span>
            : <div className="galleryCaptionTitle">{current.caption}</div>)
        : null
    const creditEl = credit
        ? (inline
            ? <span className="galleryCaptionCredit">{credit}</span>
            : <div className="galleryCaptionCredit">{credit}</div>)
        : null

    if (inline) {
        return <p className="galleryCaptionText">{title}{creditEl}</p>
    }

    return <div className="galleryCaption">{title}{creditEl}</div>
}

const GalleryArrow = (props: { direction: "left" | "right", onClick: () => void }) => (
    <button
        type="button"
        className={"galleryArrowBtn arrow" + (props.direction === "left" ? "Left" : "Right")}
        aria-label={props.direction === "left" ? "next" : "previous"}
        onClick={props.onClick}
    />
)

export const ArticleImageGalleryV2View = (props: ArticleImageGalleryV2ViewProps) => {
    const { imageList, isMobile, isApp, componentaId } = props
    const len = imageList.length
    const sliderRef: any = useRef(null)
    // Slides are rendered reversed so slickPrev advances logically and animates L→R.
    const [slickIndex, setSlickIndex] = useState(len > 0 ? len - 1 : 0)
    const currentSlide = len > 0 ? len - 1 - slickIndex : 0
    const current = imageList[currentSlide] || emptyItem
    const imagesJson = JSON.stringify(imageList)

    useEffect(() => {
        if (!sliderRef.current || len <= 0) {
            return
        }

        sliderRef.current.slickGoTo(len - 1, true)
        setSlickIndex(len - 1)
    }, [len])

    const goToLogical = (logicalIndex: number) => {
        if (sliderRef.current && len > 0) {
            sliderRef.current.slickGoTo(len - 1 - logicalIndex)
        }
    }

    const goAdvance = () => {
        if (sliderRef.current) {
            sliderRef.current.slickPrev()
        }
    }

    const goBack = () => {
        if (sliderRef.current) {
            sliderRef.current.slickNext()
        }
    }

    const settings = {
        infinite: true,
        dots: false,
        arrows: false,
        slidesToShow: 1,
        slidesToScroll: 1,
        speed: 500,
        rtl: false,
        initialSlide: len > 0 ? len - 1 : 0,
        afterChange: (index: number) => setSlickIndex(index),
    }

    const slides = [...imageList].reverse().map((image, reversedIndex) => {
        const logicalIndex = len - 1 - reversedIndex
        const imageEl = image.imageSrc
            ? <img className="galleryImage" src={image.imageSrc} alt={image.caption} />
            : <div className="galleryImagePlaceholder" />

        return (
            <div key={image.imageId || logicalIndex}>
                {isApp && componentaId && image.imageId
                    ? <a className="galleryImageLink" data-href="image_gallery" href={`image_gallery?componentaId=${componentaId}&index=${logicalIndex}&imageId=${image.imageId}&type=.jpg`}>
                        {imageEl}
                    </a>
                    : imageEl}
            </div>
        )
    })

    const slider = <Slider ref={sliderRef} {...settings}>{slides}</Slider>
    const dots = <GalleryDots imageList={imageList} currentSlide={currentSlide} onGoTo={goToLogical} />
    const arrowRight = <GalleryArrow direction="right" onClick={goBack} />
    const arrowLeft = <GalleryArrow direction="left" onClick={goAdvance} />

    return (
        <div className={`articleImageGalleryV2 ${isMobile ? " isMobile" : ""}`}>
            {isApp && componentaId && <script type="text/javascript" dangerouslySetInnerHTML={{ __html: `const galleryImagesArrayObjects_${componentaId} = ${imagesJson}` }} />}

            <div className="galleryTopLine" />

            {isMobile ? (
                <>
                    <div className="galleryMobileBody">
                        <div className="gallerySliderWrap">
                            {slider}
                        </div>

                        <GalleryCaption current={current} inline />

                        <div className="galleryThinLine" />
                    </div>

                    <div className="galleryControls">
                        {arrowRight}
                        {dots}
                        {arrowLeft}
                    </div>
                </>
            ) : (
                <>
                    <div className="galleryBody">
                        <div className="galleryImageCol">
                            {slider}
                        </div>

                        <div className="galleryMeta">
                            <div className="galleryArrows">
                                {arrowRight}
                                {arrowLeft}
                            </div>

                            <GalleryCaption current={current} />
                        </div>
                    </div>

                    {dots}
                </>
            )}

            <div className="galleryBottomLine" />
        </div>
    )
}

ArticleImageGalleryV2View.siteScriptName = "ArticleImageGalleryV2View"
